// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {produce} from 'immer';
import React from 'react';
import {toast} from 'react-toastify';

import {useUpdateRunQueueMutation} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {ExternalLink} from '../utilTypes';
import * as S from './LinksDrawerContent.styles';

export type LinksDrawerContentProps = React.PropsWithChildren<{
  closeDrawer: () => void;
  setLinks: React.Dispatch<React.SetStateAction<ExternalLink[]>>;

  links: ExternalLink[];
  queueID: string;
  open: boolean;
}>;

export const LinksDrawerContent = ({
  links,
  queueID,
  closeDrawer,
  setLinks,
}: LinksDrawerContentProps) => {
  const [updateRunQueue] = useUpdateRunQueueMutation();

  const updateLinkLabel = React.useCallback(
    (index: number, value: string) => {
      setLinks(prevLinks =>
        produce(prevLinks, draft => {
          draft[index].label = value;
        })
      );
    },
    [setLinks]
  );

  const updateLinkUrl = React.useCallback(
    (index: number, value: string) => {
      setLinks(prevLinks =>
        produce(prevLinks, draft => {
          draft[index].url = value;
        })
      );
    },
    [setLinks]
  );

  const onDelete = React.useCallback(
    (index: number) => {
      setLinks(prevLinks =>
        produce(prevLinks, draft => {
          draft.splice(index, 1);
        })
      );
    },
    [setLinks]
  );

  const onCreateNew = React.useCallback(() => {
    setLinks(prevLinks =>
      produce(prevLinks, draft => {
        draft.push({
          label: 'New Link',
          url: 'https://example.com',
        });
      })
    );
  }, [setLinks]);

  const onCloseDrawer = async () => {
    const updateQueueResp = await updateRunQueue({
      variables: {
        queueID,
        externalLinks: JSON.stringify({links}),
      },
      context: propagateErrorsContext(),
    });
    if (updateQueueResp.errors != null) {
      toast.error(`Error updating queue config: ${updateQueueResp.errors}`);
    }
    closeDrawer();
  };

  const linkForms = React.useMemo(() => {
    return links.map((link: ExternalLink, index: number) => {
      return (
        // eslint-disable-next-line react/jsx-key
        <div id={index.toString()}>
          <S.FormLabel>
            Label
            <S.RightButton>
              <Button
                icon="delete"
                variant="ghost"
                onClick={() => onDelete(index)}
              />
            </S.RightButton>
          </S.FormLabel>
          <S.FormText>
            <TextField
              value={link.label}
              onChange={value => updateLinkLabel(index, value)}
            />
          </S.FormText>
          <S.FormLabel>URL</S.FormLabel>
          <S.FormText>
            <TextField
              value={link.url}
              onChange={value => updateLinkUrl(index, value)}
            />
          </S.FormText>
          <S.UnderLine />
        </div>
      );
    });
  }, [links, updateLinkLabel, updateLinkUrl, onDelete]);

  return (
    <S.LinksDrawer>
      <S.LinksDrawerMain>
        <S.HeaderRow>
          <S.Header>Manage links</S.Header>
          <S.RightButton>
            <Button
              data-test="launch-drawer-close"
              onClick={onCloseDrawer}
              icon="close"
              size="large"
              variant="ghost"
            />
          </S.RightButton>
        </S.HeaderRow>
        <S.UnderLine />
        {links && linkForms}
        <Button
          icon="add-new"
          size="large"
          variant="secondary"
          onClick={onCreateNew}
          style={{marginTop: '40px'}}>
          Create new
        </Button>
      </S.LinksDrawerMain>
      <Button
        className="w-full"
        variant="primary"
        size="large"
        onClick={onCloseDrawer}
        style={{marginTop: '16px', marginBottom: '16px'}}>
        Done
      </Button>
    </S.LinksDrawer>
  );
};
